import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Container,
    Header,
    SpaceBetween,
    Button,
    Box,
    Tabs,
    Badge,
    Alert,
    Textarea,
} from '@cloudscape-design/components';
import Avatar from '@cloudscape-design/chat-components/avatar';
import LoadingBar from '@cloudscape-design/chat-components/loading-bar';

// ── Markdown renderer ────────────────────────────────────────────────────────
const MarkdownDoc = ({ content }) => (
    <div className="sg-markdown">
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                h1: ({ node, ...props }) => <h1 className="sg-h1" {...props} />,
                h2: ({ node, ...props }) => <h2 className="sg-h2" {...props} />,
                h3: ({ node, ...props }) => <h3 className="sg-h3" {...props} />,
                h4: ({ node, ...props }) => <h4 className="sg-h4" {...props} />,
                p:  ({ node, ...props }) => <p  className="sg-p"  {...props} />,
                ul: ({ node, ...props }) => <ul className="sg-ul" {...props} />,
                ol: ({ node, ...props }) => <ol className="sg-ol" {...props} />,
                li: ({ node, ...props }) => <li className="sg-li" {...props} />,
                strong: ({ node, ...props }) => <strong className="sg-strong" {...props} />,
                a: ({ node, href, children, ...props }) => (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sg-link"
                        {...props}
                    >
                        {children}
                    </a>
                ),
                code: ({ node, inline, ...props }) =>
                    inline
                        ? <code className="sg-inline-code" {...props} />
                        : <code className="sg-code-block" {...props} />,
                pre: ({ node, ...props }) => <pre className="sg-pre" {...props} />,
                table: ({ node, ...props }) => (
                    <div className="sg-table-wrapper">
                        <table className="sg-table" {...props} />
                    </div>
                ),
                thead: ({ node, ...props }) => <thead {...props} />,
                tbody: ({ node, ...props }) => <tbody {...props} />,
                tr:    ({ node, ...props }) => <tr    {...props} />,
                th:    ({ node, ...props }) => <th className="sg-th" {...props} />,
                td:    ({ node, ...props }) => <td className="sg-td" {...props} />,
                hr:    ({ node, ...props }) => <hr className="sg-hr" {...props} />,
                blockquote: ({ node, ...props }) => <blockquote className="sg-blockquote" {...props} />,
            }}
        >
            {content}
        </ReactMarkdown>
    </div>
);

// ── Copy + Download buttons ──────────────────────────────────────────────────
const DocActions = ({ text, filename, copyLabel = 'Copy', downloadLabel = 'Download .md' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        });
    }, [text]);

    const handleDownload = useCallback(() => {
        const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }, [text, filename]);

    return (
        <SpaceBetween direction="horizontal" size="xs">
            <Button variant="normal" iconName={copied ? 'check' : 'copy'} onClick={handleCopy}>
                {copied ? 'Copied!' : copyLabel}
            </Button>
            <Button variant="normal" iconName="download" onClick={handleDownload}>
                {downloadLabel}
            </Button>
        </SpaceBetween>
    );
};

// ── Metadata header ──────────────────────────────────────────────────────────
const MetaRow = ({ label, value }) => {
    if (!value) return null;
    const display = value === 'N/A' ? <em style={{ color: 'var(--color-text-body-secondary)' }}>unknown</em> : value;
    return (
        <Box fontSize="body-s">
            <span style={{ color: 'var(--color-text-label)', fontWeight: 600, marginRight: 6 }}>{label}:</span>
            <span style={{ color: 'var(--color-text-body-secondary)' }}>{display}</span>
        </Box>
    );
};

// ── Single result card ───────────────────────────────────────────────────────
const ResultCard = ({ result, isStreaming, streamingDoc }) => {
    const doc = isStreaming ? streamingDoc : result.doc;
    const [activeTab, setActiveTab] = useState('rendered');

    const scoreColor = result.score >= 0.8 ? 'green'
        : result.score >= 0.6 ? 'blue'
        : result.score != null ? 'red'
        : 'grey';

    return (
        <Container
            header={
                <Header
                    variant="h3"
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            {result.cached && (
                                <Badge color="grey">
                                    {result.cached_stored_at
                                        ? `Cached (${new Date(result.cached_stored_at).toLocaleDateString()})`
                                        : 'Cached'}
                                </Badge>
                            )}
                            {result.score != null && (
                                <Badge color={scoreColor}>
                                    Score: {result.score.toFixed(2)}
                                </Badge>
                            )}
                            {result.iterations != null && (
                                <Badge color="grey">
                                    {result.iterations} iter{result.iterations !== 1 ? 's' : ''}
                                </Badge>
                            )}
                            {result.arc_count > 0 && (
                                <Badge color="blue">
                                    {result.arc_count} revision{result.arc_count !== 1 ? 's' : ''}
                                </Badge>
                            )}
                            {doc && (
                                <DocActions
                                    text={doc}
                                    filename={`${(result.title || 'steering-doc').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.md`}
                                />
                            )}
                        </SpaceBetween>
                    }
                >
                    {result.title}
                </Header>
            }
        >
            {isStreaming && (
                <Box padding={{ bottom: 's' }}>
                    <SpaceBetween direction="horizontal" size="xs">
                        <Avatar
                            ariaLabel="Steering Generator"
                            color="gen-ai"
                            iconName="gen-ai"
                            tooltipText="Generating…"
                        />
                        <LoadingBar variant="gen-ai" />
                    </SpaceBetween>
                </Box>
            )}

            {/* Metadata block — always shown as soon as URL is known */}
            {result.url && (
                <div className="sg-meta-block">
                    <SpaceBetween direction="vertical" size="xxs">
                        <Box fontSize="body-s">
                            <span className="sg-meta-label">URL:</span>{' '}
                            <a href={result.url} target="_blank" rel="noopener noreferrer" className="sg-link">
                                {result.url}
                            </a>
                        </Box>
                        <MetaRow label="Authors"  value={result.authors} />
                        <MetaRow label="Published" value={result.date} />
                        <MetaRow label="Services"  value={result.services} />
                    </SpaceBetween>
                </div>
            )}

            {doc ? (
                <Tabs
                    activeTabId={activeTab}
                    onChange={({ detail }) => setActiveTab(detail.activeTabId)}
                    tabs={[
                        {
                            id: 'rendered',
                            label: 'Rendered',
                            content: <MarkdownDoc content={doc} />,
                        },
                        {
                            id: 'raw',
                            label: 'Raw Markdown',
                            content: (
                                <div style={{ position: 'relative' }}>
                                    <Textarea
                                        value={doc}
                                        readOnly
                                        rows={Math.min(40, doc.split('\n').length + 2)}
                                        spellcheck={false}
                                    />
                                </div>
                            ),
                        },
                    ]}
                />
            ) : result.error ? (
                <Alert type="error">{result.error}</Alert>
            ) : (
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    <SpaceBetween direction="vertical" size="s" alignItems="center">
                        <LoadingBar variant="gen-ai" />
                        <Box color="text-body-secondary" fontSize="body-s">
                            Pipeline running — document will appear here when ready
                        </Box>
                    </SpaceBetween>
                </Box>
            )}
        </Container>
    );
};

// ── Main viewer ──────────────────────────────────────────────────────────────
export default function SteeringDocViewer({
    results,
    streamingDocs,
    selectedResultIdx,
    setSelectedResultIdx,
}) {
    const allDocs = results.map(r => r.doc).filter(Boolean).join('\n\n---\n\n');

    return (
        <SpaceBetween direction="vertical" size="m">
            {/* Tab bar when multiple results */}
            {results.length > 1 && (
                <Container
                    header={
                        <Header
                            variant="h2"
                            actions={
                                allDocs && (
                                    <DocActions
                                        text={allDocs}
                                        filename="steering-docs-all.md"
                                        copyLabel="Copy All"
                                        downloadLabel="Download All"
                                    />
                                )
                            }
                        >
                            Generated Steering Documents ({results.length})
                        </Header>
                    }
                >
                    <Tabs
                        activeTabId={String(selectedResultIdx ?? 0)}
                        onChange={({ detail }) => setSelectedResultIdx(Number(detail.activeTabId))}
                        tabs={results.map((r, i) => ({
                            id:    String(i),
                            label: (r.title || r.url || 'Result').length > 40
                                ? (r.title || r.url || 'Result').slice(0, 38) + '…'
                                : (r.title || r.url || 'Result'),
                        }))}
                    />
                </Container>
            )}

            {/* Single result header when only one */}
            {results.length === 1 && (
                <Header variant="h2">Generated Steering Document</Header>
            )}

            {/* Active result card (selected tab or only result) */}
            {results.length > 0 && (() => {
                const idx    = selectedResultIdx ?? 0;
                const result = results[Math.min(idx, results.length - 1)];
                if (!result) return null;
                const streaming = streamingDocs[result.jobId] || '';
                const isStreaming = result.status === 'pending' && !!streaming;
                return (
                    <ResultCard
                        result={result}
                        isStreaming={isStreaming}
                        streamingDoc={streaming}
                    />
                );
            })()}
        </SpaceBetween>
    );
}
